import React, { useEffect, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCDropDownItem, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCServicesApi, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import AppIcons from "src/components/AppIcons";
import FiltersContainer from "src/components/FiltersContainer";
import LovDiagnosticModels from "src/components/Lov/DiagnosticModels";
import SidebarCard from "src/components/SidebarCard";
import { IRefSidebarCard, ISidebarCardItem } from "src/components/SidebarCard/interface";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { useParams } from "react-router-dom";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import { PermissionEnum } from "src/context/Auth/interface";
import CheckPermission from "src/components/CheckPermission";
import DiagnosticContent from "./components/DiagnosticContent";
import SidebarCards from "./components/SidebarCards";
import { IFiltersDiagnostics, IMdtbb001, IRefDiagnosticModals } from "./interface";
import Modals from "./modals";

const Diagnostics: React.FC = () => {

	const params = useParams<{
		diagnosticId?: string;
	}>();

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();

	const refSidebarCard = useRef<IRefSidebarCard>(null);
	const refModalAddOrChange = useRef<IRefDiagnosticModals>(null);

	const [selectedDiagnostic, setSelectedDiagnostic] = useState<IMdtbb001 | null>(null);
	const [diagnosticsData, setDiagnosticsData] = useState<IMdtbb001[]>([]);
	const [showFilters, setShowFilters] = useState(false);
	const [filtersDiagnostics, setfiltersDiagnostics] = useState<IFiltersDiagnostics>({ 
		filter: undefined,
		type: undefined
	});

	const { 
		handleSubmit : handleSubmitFiltersDiagnostics, 
		control: controlFiltersDiagnostics, 
		register: registerFiltersDiagnostics,
		reset: resetFiltersDiagnostics
	} = useForm();

	const queryData = async (id?: number) => {
		setLoading(true);
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "Diagnostics/List",
				urlParameters: {
					filter: filtersDiagnostics.filter ?? "",
					type: filtersDiagnostics.type ?? ""
				}
			});
			setDiagnosticsData(data);
			setLoading(false);
			if(id){
				const diagnostic : IMdtbb001 | null = data.find((x: IMdtbb001) => x.mdb001Id === id);
				if(diagnostic){
					refSidebarCard.current?.onClickCardFuncion(diagnostic.mdb001Id, diagnostic.mda001Titulo, diagnostic);
					refSidebarCard.current?.changeListVisibility();
				}
			}

		}catch(err: any){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar dados"), addToast);
		}
		return null;
	};

	const callbackChangeDiagnostic = async (id: number) => {
		await queryData(id);	
	};

	const callbackDeleteDiagnostic =  (id: number) => {
		setDiagnosticsData((prevState) => prevState.filter((x) => x.mdb001Id !== id));
		if(selectedDiagnostic && selectedDiagnostic.mdb001Id === id){
			setSelectedDiagnostic(null);
		}
	};

	const onClickCardFuncion = (id: number) => {
		const selectedCard = diagnosticsData.find((x) => x.mdb001Id === id);
		if(selectedCard){
			setSelectedDiagnostic(selectedCard);
			refSidebarCard.current?.changeListVisibility();
		}
		else
			setSelectedDiagnostic(null);
	};

	const submitFiltersDiagnostics = (data: any) => {
		setSelectedDiagnostic(null);
		setfiltersDiagnostics({
			filter: data.filter,
			type: typeof data.type === "object" ? data.type?.value : data.type
		});
	};

	useEffect(() => {
		const initialDiagnostic = params?.diagnosticId ? parseInt(params?.diagnosticId, 10) : undefined;
		queryData(initialDiagnostic);
	},[]);

	useEffectSkipInitialRender(() => {
		queryData();
	},[filtersDiagnostics]);

	return (
		<>
			<SidebarCard 
				ref={refSidebarCard}
				icon={() => <AppIcons name="businessDiagnosis"/>}
				title={translate("Diagnósticos de Negócio")}
				cardClick={(id) => {
					const selectedCard = diagnosticsData.find((x) => x.mdb001Id === id);
					if(selectedCard)
						setSelectedDiagnostic(selectedCard);
					else
						setSelectedDiagnostic(null);
				}}
				options={(card: ISidebarCardItem) => (
					<>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							<RCCDropDownItem
								selfEnclose
								onClick={ (e) => {e.stopPropagation(); refModalAddOrChange.current?.actionButtonChange(card.id); } }
							>
								<RCCIcon name="pencil" 
								/>
								{translate("Alterar")}
							</RCCDropDownItem>		
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
								<RCCDropDownItem 
									selfEnclose
									onClick={(e) => {e.stopPropagation(); refModalAddOrChange.current?.actionButtonDelete(card.id, card.title);}}
								>
									<RCCIcon name="trash" 
									/>
									{translate("Excluir")}
								</RCCDropDownItem>			
							</CheckPermission>						
						</CheckPermission>		
				  </>
				)}
				filterContent={
					<>
						<FiltersContainer
							onSubmitFilters={handleSubmitFiltersDiagnostics(submitFiltersDiagnostics)}
							resetFilters={() => resetFiltersDiagnostics({ 
								filter: "",
								type: ""
							})}
							showFilters={showFilters}
						>
							<RCCGrid
								container 
								item
								xs={12}
							>
								<LovDiagnosticModels 
									control={controlFiltersDiagnostics}
									name="type"
									label={translate("Modelo de diagnóstico de negócio")}
									defaultValue={filtersDiagnostics.type}
									submit="value and label"
									isClearable
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
							>
								<RCCInput 
									register={registerFiltersDiagnostics}
									name="filter"
									label={translate("Pesquisar")}
									defaultValue={filtersDiagnostics.filter}
								/>
							</RCCGrid>
						</FiltersContainer>
					</>
				}
				buttonsContent={
					<>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							<RCCTooltip
								text={translate("Adicionar Diagnóstico")}
								color="purple"
							>
								<RCCButton
									icon={{name: "plus", color: "#FFF", size: 16}}
									color="purple"
									layout="isRounded"
									size="md"
									onClick={() => refModalAddOrChange.current?.actionButtonAdd()}
								/>
							</RCCTooltip>										
						</CheckPermission>
						<RCCTooltip
							text={translate("Filtros")}
							color={showFilters || filtersDiagnostics.filter || filtersDiagnostics.type ? "blue" : "grey"}
						>
							<RCCButton
								icon={{name: "filter", color: "#FFF", size: 16}}
								color={showFilters || filtersDiagnostics.filter || filtersDiagnostics.type ? "blue" : "grey"}
								layout="isRounded"
								size="md"
								onClick={() => setShowFilters((prevState) => !prevState)}
							/>
						</RCCTooltip>
					</>
				}
				customCards={
					<>
						{!diagnosticsData || diagnosticsData.length <= 0 ? (
							<RCCAlert
								description={translate("Não foram encontrados dados")}
							/>
						): (
							<SidebarCards
								items={diagnosticsData}
								setItems={setDiagnosticsData}
								refModalAddChangeDelete={refModalAddOrChange}
								onClickCardFuncion={onClickCardFuncion}
								selectedDiagnostic={selectedDiagnostic ?? undefined}
							/>
						)}
					</>
				}
				content={
					<>
						{selectedDiagnostic ? (
							<>
								<DiagnosticContent mdtbb001={selectedDiagnostic} />
							</>
						) : (
							<RCCAlert
								title={translate("Nenhum Diagnóstico de Negócio selecionado")}
								description={translate("Selecione uma diagnóstico para ver seus detalhes")}
							/>
						)}
					</>
				}
			/>

			<Modals 
				ref={refModalAddOrChange}
				callbackAddOrChange={callbackChangeDiagnostic}
				callbackDelete={callbackDeleteDiagnostic}
			/>

		</>
	);
};

export default Diagnostics;
